import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTvPairing } from "@/context/TvPairingContext";
import { tvQrInfo } from "@/mocks/tvApp";

type FocusTarget = 'back' | 'generate' | 'rename' | 'disconnect' | 'connect-sim';

export default function TvQrPage() {
  const navigate = useNavigate();
  const { session, isDemoMode, generateNewCode, connectDevice, disconnectDevice, renameDevice } = useTvPairing();
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(300);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(session.deviceName);
  const [focused, setFocused] = useState<FocusTarget>('back');

  const { state, pairingCode, deviceName, expiresAt } = session;

  // ── Countdown for WAITING state ──
  useEffect(() => {
    if (state !== 'WAITING') return;
    const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
    setCountdown(remaining);
    if (remaining <= 0) return;
    const t = setInterval(() => {
      const r = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setCountdown(r);
      if (r <= 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [state, expiresAt]);

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;
  const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(pairingCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const handleRename = () => {
    if (nameInput.trim()) {
      renameDevice(nameInput.trim());
    }
    setEditingName(false);
  };

  // ── Keyboard navigation ──
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (editingName) return;

      if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault();
        navigate('/tv-app');
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (focused === 'back') navigate('/tv-app');
        else if (focused === 'generate') generateNewCode();
        else if (focused === 'rename') setEditingName(true);
        else if (focused === 'disconnect') disconnectDevice();
        else if (focused === 'connect-sim') connectDevice();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [focused, editingName, navigate, generateNewCode, disconnectDevice, connectDevice]);

  // ── State badge ──
  const stateBadge = () => {
    switch (state) {
      case 'WAITING':
        return { label: 'ESPERANDO', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: 'ri-time-line', pulse: true };
      case 'CONNECTED_MOCK':
        return { label: 'CONECTADO (DEMO)', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: 'ri-check-double-line', pulse: false };
      case 'EXPIRED':
        return { label: 'EXPIRADO', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: 'ri-timer-flash-line', pulse: false };
      case 'DISCONNECTED':
        return { label: 'DESCONECTADO', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: 'ri-shut-down-line', pulse: false };
      case 'ERROR':
        return { label: 'ERROR', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: 'ri-error-warning-line', pulse: false };
    }
  };
  const badge = stateBadge();

  // ── Focusable buttons list ──
  const focusableButtons: FocusTarget[] = [];
  if (state === 'WAITING' || state === 'EXPIRED') focusableButtons.push('generate');
  if (state === 'CONNECTED_MOCK') {
    focusableButtons.push('rename');
    focusableButtons.push('disconnect');
    focusableButtons.push('connect-sim');
  }
  if (state === 'DISCONNECTED' || state === 'ERROR') focusableButtons.push('generate');

  return (
    <div className="relative min-h-screen bg-[#0a0a0f] text-white overflow-hidden select-none">
      {/* Ambient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-500/5 via-transparent to-orange-500/5"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.03)_0%,_transparent_70%)]"></div>

      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="absolute top-0 left-0 right-0 z-20 bg-amber-500/10 border-b border-amber-500/20 px-6 py-2 flex items-center justify-center gap-2">
          <i className="ri-flask-line text-amber-400 text-sm"></i>
          <span className="text-xs text-amber-400 font-semibold">MODO DEMOSTRACIÓN — El pairing no es persistente entre dispositivos reales</span>
        </div>
      )}

      <div className="relative z-10 h-screen flex flex-col items-center justify-center px-8 pt-10">
        {/* Back button */}
        <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-8 lg:px-16 py-4">
          <button
            onClick={() => navigate('/tv-app')}
            className={`flex items-center gap-3 px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap outline-none text-lg font-semibold ${
              focused === 'back'
                ? 'bg-white text-[#0a0a0f] ring-4 ring-white/30'
                : 'bg-white/10 hover:bg-white/15 text-white'
            }`}
          >
            <i className="ri-arrow-left-line text-xl"></i>
            Volver
          </button>
          {/* State badge */}
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-bold ${badge.color}`}>
            {badge.pulse && <span className="w-2 h-2 rounded-full bg-current animate-pulse"></span>}
            <i className={badge.icon}></i>
            {badge.label}
          </div>
        </div>

        {/* ── CONNECTED_MOCK View ── */}
        {state === 'CONNECTED_MOCK' && (
          <div className="flex flex-col items-center gap-8 max-w-lg w-full mt-6">
            <div className="w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <i className="ri-smartphone-line text-emerald-400 text-3xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-white text-center">Dispositivo Vinculado</h1>
            <p className="text-base text-white/60 text-center">Tu control remoto está listo. Usá el celular como control remoto desde la misma sesión de prueba.</p>

            {/* Device info card */}
            <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/40">Dispositivo</span>
                {editingName ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditingName(false); }}
                      className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-white/40 w-48"
                      autoFocus
                    />
                    <button onClick={handleRename} className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center cursor-pointer"><i className="ri-check-line"></i></button>
                    <button onClick={() => setEditingName(false)} className="w-8 h-8 rounded-lg bg-white/10 text-white/50 flex items-center justify-center cursor-pointer"><i className="ri-close-line"></i></button>
                  </div>
                ) : (
                  <span className="text-base font-bold text-white flex items-center gap-2">
                    {deviceName}
                    <button
                      onClick={() => { setNameInput(deviceName); setEditingName(true); }}
                      className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/15 text-white/50 hover:text-white flex items-center justify-center cursor-pointer transition-all"
                    >
                      <i className="ri-pencil-line text-xs"></i>
                    </button>
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/40">Código</span>
                <span className="text-base font-mono font-bold text-white/70 tracking-wider">{pairingCode}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/40">Vinculado</span>
                <span className="text-sm text-white/60">
                  {session.connectedAt ? new Date(session.connectedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => navigate('/tv-app/remote')}
                className="flex items-center gap-2 px-6 py-4 rounded-2xl bg-accent-400 text-[#0a0a0f] font-bold text-base cursor-pointer whitespace-nowrap transition-all hover:bg-accent-300 outline-none focus:ring-4 focus:ring-accent-400/30"
              >
                <i className="ri-remote-control-line text-lg"></i>
                Abrir Control Remoto
              </button>
              <button
                onClick={disconnectDevice}
                className={`flex items-center gap-2 px-6 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${
                  focused === 'disconnect'
                    ? 'bg-white text-[#0a0a0f] ring-4 ring-white/30'
                    : 'bg-white/10 hover:bg-white/15 text-white'
                }`}
              >
                <i className="ri-link text-lg"></i>
                Desvincular
              </button>
            </div>
          </div>
        )}

        {/* ── WAITING / EXPIRED / DISCONNECTED / ERROR View ── */}
        {state !== 'CONNECTED_MOCK' && (
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 max-w-4xl w-full mt-6">
            {/* QR Code */}
            <div className="relative flex-shrink-0">
              <div className={`w-64 h-64 lg:w-80 lg:h-80 rounded-3xl bg-white p-4 shadow-2xl ${state === 'EXPIRED' ? 'opacity-40 blur-sm' : state === 'ERROR' ? 'opacity-50' : ''}`}>
                <img
                  src={tvQrInfo.qrImage}
                  alt="QR Code para vincular TV"
                  className="w-full h-full object-contain rounded-2xl"
                />
              </div>
              {state === 'EXPIRED' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-red-500/90 text-white px-6 py-3 rounded-xl text-lg font-bold backdrop-blur">EXPIRADO</div>
                </div>
              )}
              {state === 'ERROR' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-red-500/90 text-white px-6 py-3 rounded-xl text-lg font-bold backdrop-blur flex items-center gap-2">
                    <i className="ri-error-warning-line"></i> ERROR
                  </div>
                </div>
              )}
              {state === 'DISCONNECTED' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-gray-500/90 text-white px-6 py-3 rounded-xl text-lg font-bold backdrop-blur">DESCONECTADO</div>
                </div>
              )}
              {state === 'WAITING' && (
                <div className="absolute -inset-3 rounded-[28px] border-2 border-accent-400/30 animate-pulse pointer-events-none"></div>
              )}
            </div>

            {/* Instructions */}
            <div className="text-center lg:text-left flex-1 max-w-md">
              <div className="flex items-center gap-3 justify-center lg:justify-start mb-4">
                <div className="w-12 h-12 rounded-2xl bg-accent-400/10 flex items-center justify-center">
                  <i className="ri-qr-code-line text-accent-400 text-2xl"></i>
                </div>
                <h1 className="text-3xl font-bold text-white">Vincular TV</h1>
              </div>

              <p className="text-base text-white/70 leading-relaxed mb-6">
                Escaneá este código QR con la app de RadioFrecuenciaWeb en tu celular para vincular tu cuenta.
              </p>

              {/* Pairing Code */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
                <p className="text-sm text-white/40 mb-2">Código de vinculación</p>
                <div className="flex items-center gap-4">
                  <span className="text-3xl font-mono font-bold text-white tracking-[0.3em]">{pairingCode}</span>
                  <button
                    onClick={handleCopyCode}
                    className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/15 flex items-center justify-center cursor-pointer transition-all"
                  >
                    <i className={`text-white text-lg ${copied ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                  </button>
                </div>
                {copied && (
                  <p className="text-sm text-emerald-400 mt-2 flex items-center gap-1">
                    <i className="ri-check-line"></i>
                    ¡Copiado!
                  </p>
                )}
              </div>

              {/* Device Name */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
                <p className="text-sm text-white/40 mb-2">Nombre del dispositivo</p>
                {editingName ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditingName(false); }}
                      className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-white/40 flex-1"
                      autoFocus
                    />
                    <button onClick={handleRename} className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center cursor-pointer"><i className="ri-check-line"></i></button>
                    <button onClick={() => setEditingName(false)} className="w-8 h-8 rounded-lg bg-white/10 text-white/50 flex items-center justify-center cursor-pointer"><i className="ri-close-line"></i></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-white">{deviceName}</span>
                    <button
                      onClick={() => { setNameInput(deviceName); setEditingName(true); }}
                      className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/15 text-white/50 hover:text-white flex items-center justify-center cursor-pointer transition-all"
                    >
                      <i className="ri-pencil-line text-xs"></i>
                    </button>
                  </div>
                )}
              </div>

              {/* Timer / Status */}
              {state === 'WAITING' && (
                <div className="flex items-center gap-3 mb-8 justify-center lg:justify-start">
                  <i className={`text-lg ${countdown < 60 ? 'ri-timer-line text-amber-400' : 'ri-timer-line text-white/50'}`}></i>
                  <span className={`text-lg font-mono font-bold ${countdown < 60 ? 'text-amber-400' : 'text-white/70'}`}>
                    {timeStr}
                  </span>
                </div>
              )}

              {/* Steps */}
              <div className="space-y-3 text-left mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-white">1</span>
                  </div>
                  <p className="text-sm text-white/50">Abrí la app de RFW en tu celular</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-white">2</span>
                  </div>
                  <p className="text-sm text-white/50">Andá a <strong className="text-white/70">Configuración</strong> &gt; <strong className="text-white/70">Vincular TV</strong></p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-white">3</span>
                  </div>
                  <p className="text-sm text-white/50">Escaneá el QR o ingresá el código manualmente</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-white">4</span>
                  </div>
                  <p className="text-sm text-white/50">¡Listo! Tu TV queda vinculada sin contraseñas</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-6 px-8 lg:px-16 py-8">
          <button
            onClick={() => navigate('/tv-app')}
            className={`flex items-center gap-2 px-6 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${
              focused === 'back'
                ? 'bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105'
                : 'bg-white/10 hover:bg-white/15 text-white'
            }`}
          >
            <i className="ri-close-line text-lg"></i>
            Cancelar
          </button>

          {(state === 'WAITING') && (
            <button
              onClick={() => connectDevice()}
              tabIndex={0}
              className="flex items-center gap-3 px-10 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-lg font-bold bg-emerald-500 text-white hover:bg-emerald-400 focus:ring-4 focus:ring-emerald-500/30"
            >
              <i className="ri-link text-xl"></i>
              Simular Conexión (Demo)
            </button>
          )}

          {(state === 'EXPIRED' || state === 'DISCONNECTED' || state === 'ERROR') && (
            <button
              onClick={generateNewCode}
              className={`flex items-center gap-3 px-10 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-lg font-bold ${
                focused === 'generate'
                  ? 'bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105'
                  : 'bg-accent-400 text-[#0a0a0f] hover:bg-accent-300'
              }`}
            >
              <i className="ri-refresh-line text-xl"></i>
              Generar Nuevo Código
            </button>
          )}
        </div>
      </div>
    </div>
  );
}